import {
  ArrowRightIcon,
  Box,
  ButtonGroup,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  FormControlLabel,
  Grid,
  IconButton,
  LabelValueList,
  Tooltip,
  Typography,
  useModal,
  useTheme,
} from '@forgeglobal/design-framework'
import { BidAskDetailModal } from 'components/order-book/BidAskDetailModal'
import {
  ListingStructureType,
  useIssuerTradeSummaryQuery,
  useOrderBookDetailsQuery,
} from 'graphql/bindings/AdminBindings'
import { SupplyDemandGraphBase } from 'components/issuers/IssuerSupplyDemandGraph'
import { TradeDetail } from 'components/issuers/TradeDetail'
import { formatAbbreviatedMoney } from 'util/NumberUtils'
import React, { Fragment, useState } from 'react'

interface SummaryData {
  companyId: number
  actionableOnly: boolean
  structures: string[]
  volume: number
  count: number
  isBid?: boolean
}

const TradeIntentSummary: React.FC<SummaryData> = ({ companyId, actionableOnly, structures, volume, count, isBid }) => {
  const [showModal, hideModal] = useModal(() => {
    const title = `${isBid ? 'Demand' : 'Supply'} Breakdown: `
    const type = isBid ? 'bid' : 'ask'
    const status = actionableOnly ? 'actionable' : 'active'
    return (
      <BidAskDetailModal
        hideModal={hideModal}
        title={title}
        type={type}
        companyId={companyId}
        status={status}
        structures={structures}
        count={count}
      />
    )
  }, [actionableOnly, isBid, structures])

  return (
    <Card>
      <CardHeader
        title={`${isBid ? 'Bid' : 'Ask'} Details`}
        action={
          count > 0 && (
            <Tooltip title="View Opportunities" placement="top">
              <IconButton onClick={showModal}>
                <ArrowRightIcon />
              </IconButton>
            </Tooltip>
          )
        }
      />

      <CardContent>
        <LabelValueList
          items={[
            {
              label: 'Total Volume',
              value: formatAbbreviatedMoney(volume),
            },
            {
              label: `Active ${isBid ? 'Bid' : 'Ask'}s`,
              value: count.toString(),
            },
          ]}
        />
      </CardContent>
    </Card>
  )
}

const GeneralSummary: React.FC<{ accountId: string }> = ({ accountId }) => {
  const { data } = useIssuerTradeSummaryQuery({ variables: { id: accountId } })
  const lastDeal = data?.account?.issuer_account?.deals?.[0]

  return (
    <Card>
      <CardHeader title={`Last Trade Details`} />
      <CardContent>{lastDeal && <TradeDetail deal={lastDeal} />}</CardContent>
    </Card>
  )
}

const StructureToggle: React.FC<{ value: string[]; onChange: (newValue: string[]) => void }> = ({
  value,
  onChange,
}) => {
  const handleChange = (event: React.MouseEvent<HTMLElement>, newAlignment: string[]) => {
    if (value.includes('all') && newAlignment.length > 1) {
      onChange(newAlignment.filter(val => val !== 'all'))
    } else if (newAlignment.includes('all') && !value.includes('all')) {
      onChange(['all'])
    } else if (newAlignment.length === 0) {
      onChange(['all'])
    } else {
      onChange(newAlignment)
    }
  }
  return (
    <ButtonGroup
      onChange={handleChange}
      value={value}
      options={[
        { label: 'All', value: 'all' },
        { label: 'Direct', value: 'direct_share_transfer' },
        { label: 'SPV', value: 'share_backed_fund_interest' },
        { label: 'Forward', value: 'forward_backed_fund_interest' },
      ]}
      size={'small'}
    />
  )
}

export const OrderBookExpandedContent: React.FC<{ companyId: number }> = ({ companyId }) => {
  const [structures, changeStructures] = useState<string[]>(['all'])
  const [qualifiedOnly, setQualifiedOnly] = useState<boolean>(true)
  const theme = useTheme()
  const { data, loading } = useOrderBookDetailsQuery({
    variables: {
      companyId: companyId,
      qualifiedOnly: qualifiedOnly,
      structures: structures.includes('all') ? undefined : (structures as ListingStructureType[]),
    },
  })
  const qualifiedCheckClicked = () => {
    setQualifiedOnly(!qualifiedOnly)
  }
  const company = data?.company
  return (
    <Fragment>
      <Box mb={3} alignItems="center" display="flex" justifyContent="space-between">
        <Typography variant="h5" display="inline">
          Issuer Snapshot
        </Typography>
        <div>
          <Tooltip title="Status is Qualified or Pre-closings" placement="top">
            <FormControlLabel
              control={<Checkbox checked={qualifiedOnly} onChange={qualifiedCheckClicked} />}
              label={
                <Typography variant="caption" color="textSecondary" style={{ fontWeight: 400 }}>
                  Actionable Only?
                </Typography>
              }
              labelPlacement="start"
              style={{ marginRight: theme.spacing(2) }}
            />
          </Tooltip>
          <Typography variant="caption" color="textSecondary" style={{ marginRight: 16, fontWeight: 400 }}>
            Structure:
          </Typography>
          <StructureToggle value={structures} onChange={changeStructures} />
        </div>
      </Box>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Grid container direction="row" spacing={3}>
          <Grid item sm={4}>
            <SupplyDemandGraphBase
              orderBookAskBlocks={company?.order_book_ask_blocks ?? []}
              orderBookBidBlocks={company?.order_book_bid_blocks ?? []}
            />
          </Grid>
          <Grid item sm={4}>
            <TradeIntentSummary
              companyId={company?.id ?? 0}
              volume={company?.total_bid_volume ?? 0}
              count={company?.active_bids_count ?? 0}
              structures={structures}
              actionableOnly={qualifiedOnly}
              isBid
            />
            <Box mt={2}>
              <TradeIntentSummary
                companyId={company?.id ?? 0}
                volume={company?.total_ask_volume ?? 0}
                count={company?.active_asks_count ?? 0}
                structures={structures}
                actionableOnly={qualifiedOnly}
              />
            </Box>
          </Grid>
          <Grid item sm={4}>
            <GeneralSummary accountId={data?.company.crm_account.id} />
          </Grid>
        </Grid>
      )}
    </Fragment>
  )
}
